import { pinyin } from "pinyin-pro"
import { graphqlClient } from "@/lib/graphql"
import { graphql } from "@/__generated__"
import { isSignedLanguage } from "@/lib/signedLanguages"

// Quick word lookup for the in-lesson notes popover. Three independent, cheap
// sources, none of which block the others:
//   - reading: client-side pinyin (Chinese only) — instant, no network
//   - definition: server lookup (cache / existing flashcard / CC-CEDICT) — AI-free
//   - pronunciation: the browser's SpeechSynthesis — instant, no network

export interface WordDefinition {
  text: string
  definition: string | null
  reading: string | null
  source: string | null
}

const WORD_DEFINITION = graphql(`
  query WordDefinitionLookup($lessonId: ID!, $text: String!) {
    wordDefinition(lessonId: $lessonId, text: $text) {
      text
      definition
      reading
      source
    }
  }
`)

// Server-side definition. Returns null when nothing is known (the popover then
// shows just the reading + the flashcard action).
export async function lookupWord(lessonId: string, text: string): Promise<WordDefinition | null> {
  const res = await graphqlClient.request(WORD_DEFINITION, { lessonId, text })
  return (res.wordDefinition as WordDefinition | null) ?? null
}

const HAN = /\p{Script=Han}/u

// Contextual pinyin for a Chinese selection, with tone marks (e.g. 你好 -> "nǐ hǎo").
// Returns null when readings don't apply: not a Chinese lesson, or no Han chars in
// the selection (pinyin-pro would otherwise echo Latin text back unchanged).
export function readingFor(text: string, targetLanguageCode?: string): string | null {
  if (targetLanguageCode !== "zh") return null
  if (!HAN.test(text)) return null
  const reading = pinyin(text, { toneType: "symbol", type: "string" }).trim()
  return reading.length > 0 ? reading : null
}

// IANA-ish locale BCP-47 tags for the speech voices we care about. Falls back to
// the bare code, which most engines still resolve to a default regional voice.
const SPEECH_LOCALES: Record<string, string> = {
  zh: "zh-CN", yue: "zh-HK", ja: "ja-JP", ko: "ko-KR", en: "en-US", es: "es-ES", fr: "fr-FR", de: "de-DE",
  pt: "pt-PT", hi: "hi-IN", ar: "ar-SA", it: "it-IT", ru: "ru-RU", tr: "tr-TR", pl: "pl-PL", nl: "nl-NL",
  sv: "sv-SE", id: "id-ID", uk: "uk-UA", ro: "ro-RO", th: "th-TH", vi: "vi-VN", sw: "sw-KE", he: "he-IL",
  el: "el-GR", cs: "cs-CZ", da: "da-DK", fi: "fi-FI", hu: "hu-HU", nb: "nb-NO", no: "nb-NO", ms: "ms-MY",
  ta: "ta-IN", te: "te-IN", bn: "bn-IN", fa: "fa-IR", ur: "ur-PK", fil: "fil-PH", tl: "fil-PH", ca: "ca-ES",
  hr: "hr-HR", sk: "sk-SK", bg: "bg-BG", sr: "sr-RS", sl: "sl-SI", lt: "lt-LT", lv: "lv-LV", et: "et-EE",
}

export function speechSupported(): boolean {
  return typeof window !== "undefined" && typeof window.speechSynthesis !== "undefined"
}

// Speak the selection in the target language. Best-effort: silently no-ops where
// SpeechSynthesis is unavailable. Cancels any in-flight utterance so rapid taps
// don't queue up.
export function speakWord(text: string, targetLanguageCode?: string): void {
  if (!speechSupported()) return
  // Signed languages have no spoken form — reading the (English-gloss) text
  // aloud in a default voice would be nonsense, not pronunciation.
  if (isSignedLanguage(targetLanguageCode)) return
  const utterance = new SpeechSynthesisUtterance(text)
  if (targetLanguageCode) utterance.lang = SPEECH_LOCALES[targetLanguageCode] ?? targetLanguageCode
  window.speechSynthesis.cancel()
  window.speechSynthesis.speak(utterance)
}
